/**
 * Predsieň online zápasu v rozložení VS (docs/design/4-vyber-bojovnikov.png).
 *
 * - Zakladateľ čaká: vľavo jeho hrdina, v strede odkaz pre súpera, vpravo
 *   voľné miesto s tajným výberom.
 * - Pozvaný hráč: vľavo jeho voľba, v strede súpiska, vpravo tí, čo už sedia.
 *
 * Keď sa posledný hráč pripojí, príde push cez WebSocket a obrazovka sa
 * prepne na hru sama.
 */

import type { HeroDef } from '@unmatched/engine';
import { content } from '@unmatched/content';
import { IMPLEMENTED_HEROES } from '@unmatched/engine';
import { useEffect, useState } from 'react';

import { matches, type MatchView } from '../api.js';
import type { Connection } from '../game/useServerGame.js';
import { DeckDialog } from '../ui/DeckDialog.js';
import { HeroPanel, SecretPanel } from '../ui/HeroPanel.js';
import { Logo } from '../ui/Logo.js';
import { MapThumb } from '../ui/MapThumb.js';
import { Roster } from '../ui/Roster.js';

interface Props {
  view: MatchView;
  connection: Connection;
  busy: boolean;
  onJoin: (heroId: string) => void;
  onLeave: () => void;
}

const CONN_LABEL: Record<Connection, string> = {
  online: 'Spojenie live',
  connecting: 'Pripájam…',
  offline: 'Offline',
};

export function MatchLobby({ view, connection, busy, onJoin, onLeave }: Props) {
  const [heroes, setHeroes] = useState<HeroDef[]>([]);
  const [picked, setPicked] = useState<string | null>(null);
  const [deckHero, setDeckHero] = useState<HeroDef | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    void matches.options().then((options) => {
      setHeroes(options.heroes);
      const playable = options.heroes.find((h) => IMPLEMENTED_HEROES.includes(h.id));
      setPicked((current) => current ?? playable?.id ?? null);
    });
  }, []);

  const inviteUrl = `${window.location.origin}/#/unmatched/g/${view.id}`;
  const map = content.maps[view.mapId];
  const formatLabel =
    view.format === 'teams' ? 'Tímy 2v2' : view.format === 'ffa' ? 'Každý za seba' : 'Duel 1v1';
  const taken = view.seats.filter((s) => s.nick).length;

  /** Moje miesto: pri pozvánke prvé voľné, inak to, ktoré mám. */
  const mySlot = view.canJoin
    ? (view.seats.find((s) => !s.nick)?.slot ?? 0)
    : (view.seats.find((s) => s.isYou)?.slot ?? 0);
  const mySeat = view.seats.find((s) => s.slot === mySlot);
  const myHero = view.canJoin
    ? (heroes.find((h) => h.id === picked) ?? null)
    : (heroes.find((h) => h.id === mySeat?.heroId) ?? null);
  const others = view.seats.filter((s) => s.slot !== mySlot);

  const seatName = (slot: number, nick: string | null) =>
    `${slot + 1}P${view.format === 'teams' ? ` · tím ${slot % 2 === 0 ? 'A' : 'B'}` : ''}${
      nick ? ` · ${nick}` : ''
    }`;

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="shell">
      <header className="appHeader">
        <button type="button" className="appHeader__brand" onClick={onLeave}>
          <Logo />
        </button>
        <span className="tableChip">
          <span className="dot dot--primary" /> Stôl {view.id.slice(0, 8)}
          <small>Prípravná miestnosť pred bojom</small>
        </span>
        <div className="appHeader__right">
          <span className={`connChip connChip--${connection}`}>
            <span className="dot" /> {CONN_LABEL[connection]}
          </span>
          <button type="button" className="btn btn--ghost" onClick={onLeave}>
            Späť do klubovne
          </button>
        </div>
      </header>

      <main className="shell__main draft">
        <section className="panel draft__bar">
          <span className="tag tag--primary">{view.canJoin ? 'Pozvánka' : 'Čaká sa'}</span>
          <h1 className="draft__title">
            {formatLabel} <i>//</i> {view.canJoin ? 'Vyber si bojovníka' : 'Čaká sa na súpera'}
          </h1>
          {view.isPublic ? <span className="tag tag--primary">Verejný stôl</span> : null}
          <span className="tag">
            Pripojení <b>{taken}</b> z {view.seatsTotal}
          </span>
        </section>

        <div className="draft__stage">
          <HeroPanel
            hero={myHero}
            slot={mySlot}
            seat={view.canJoin ? `${mySlot + 1}P · ty` : seatName(mySlot, mySeat?.nick ?? null)}
            status={
              view.canJoin ? (
                <span className="tag tag--primary">Vyberáš</span>
              ) : (
                <span className="tag tag--ok">✓ Locked in</span>
              )
            }
            onShowDeck={setDeckHero}
          />

          <div className="draft__center">
            <div className="vsBadge">
              <b>VS</b>
              <span>{view.canJoin ? 'Pozvánka' : 'Čaká sa'}</span>
            </div>

            {view.canJoin ? (
              <Roster
                heroes={heroes}
                picks={view.seats.map((s) => (s.slot === mySlot ? picked : s.heroId))}
                activeSlot={mySlot}
                onPick={setPicked}
                onInspect={setDeckHero}
              />
            ) : (
              <div className="invitePanel">
                <span className="panel__title panel__title--sm">Odkaz pre súpera</span>
                {view.isPublic ? (
                  <p className="muted">Stôl je verejný — v klubovni ho vidí každý a pridať sa môže ktokoľvek.</p>
                ) : null}
                <p className="muted">
                  Pošli ho {view.seatsFree > 1 ? 'ostatným hráčom' : 'súperovi'} do chatu. Len čo si
                  {view.seatsFree > 1 ? ' všetci vyberú' : ' vyberie'} postavu, hra začne sama — nič
                  netreba obnovovať.
                </p>
                <div className="copyField">
                  <input readOnly value={inviteUrl} onFocus={(e) => e.currentTarget.select()} />
                  <button type="button" className="btn btn--primary btn--sm" onClick={copyLink}>
                    {copied ? '✓ Skopírované' : 'Kopírovať'}
                  </button>
                </div>
                <p className="waiting">
                  <span className="spinner" /> Čaká sa na {view.seatsFree}{' '}
                  {view.seatsFree === 1 ? 'hráča' : 'hráčov'}…
                </p>
              </div>
            )}
          </div>

          <div className={`draft__others ${others.length > 1 ? 'is-many' : ''}`}>
            {others.map((seat) =>
              seat.heroId ? (
                <HeroPanel
                  key={seat.slot}
                  hero={heroes.find((h) => h.id === seat.heroId) ?? null}
                  slot={seat.slot}
                  seat={seatName(seat.slot, seat.nick)}
                  compact={others.length > 1}
                  status={<span className="tag tag--ok">✓ Pripravený</span>}
                  onShowDeck={setDeckHero}
                />
              ) : (
                <SecretPanel
                  key={seat.slot}
                  slot={seat.slot}
                  seat={seatName(seat.slot, null)}
                  title={seat.nick ? 'Vyberá postavu…' : 'Voľné miesto'}
                >
                  <span className="secret__note">
                    {seat.nick
                      ? 'Voľbu uvidíš pri vstupe do arény.'
                      : 'Obsadí ho ten, kto prvý otvorí odkaz.'}
                  </span>
                </SecretPanel>
              ),
            )}
          </div>
        </div>

        <section className="panel draft__launch">
          <div className="draft__summary">
            {map ? (
              <span className="draft__map">
                <MapThumb map={map} width={120} />
                <span>
                  <span className="eyebrow">Aréna</span>
                  <b>{map.name}</b>
                </span>
              </span>
            ) : null}
            <span className="tag">{formatLabel}</span>
          </div>
          <div className="draft__actions">
            {view.canJoin ? (
              <button
                type="button"
                className="btn btn--primary btn--xl"
                disabled={!picked || busy}
                onClick={() => picked && onJoin(picked)}
              >
                {busy ? 'Pripájam…' : '⚔ Pripojiť sa a začať'}
              </button>
            ) : (
              <span className="muted">Hra sa spustí automaticky.</span>
            )}
          </div>
        </section>
      </main>

      {deckHero ? <DeckDialog hero={deckHero} onClose={() => setDeckHero(null)} /> : null}
    </div>
  );
}
